import { useState, useEffect } from 'react';

/**
 * Determines whether the visible viewport height is below the threshold
 * due to zoom (not keyboard).
 *
 * Distinguishes pinch-zoom from keyboard by checking visualViewport.scale:
 * - Pinch-zoom reduces height AND increases scale (> 1)
 * - Keyboard reduces height but scale remains 1
 * - Desktop browser zoom reduces window.innerHeight directly
 */
function checkVisualViewport(threshold: number): boolean {
  const visualViewport = window.visualViewport;
  if (visualViewport) {
    return (visualViewport.height < threshold && visualViewport.scale > 1) || window.innerHeight < threshold;
  }
  // Fallback for browsers without Visual Viewport API
  return window.innerHeight < threshold;
}

/**
 * useShortVisualViewport
 *
 * Returns true when the visible viewport height falls below the given
 * threshold due to browser zoom (desktop) or pinch-to-zoom (mobile).
 * Does NOT trigger when the on-screen keyboard opens.
 *
 * Uses the Visual Viewport API to detect mobile pinch-zoom, falling back
 * to window.innerHeight for browsers without support.
 *
 * @param threshold - Height in CSS pixels below which the viewport is considered short
 * @returns Whether the viewport is currently short due to zoom
 */
export function useShortVisualViewport(threshold: number): boolean {
  const [isShort, setIsShort] = useState(() => checkVisualViewport(threshold));

  useEffect(() => {
    const update = () => setIsShort(checkVisualViewport(threshold));
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, [threshold]);

  return isShort;
}
